import { addDays, format, startOfMonth, subMonths } from "date-fns";

// Camelized `ProjectCostBreakdownResponse` from
// GET /api/cost-and-usage/v1/projects/{uuid}/cost/breakdown.
// Costs are Decimals, which the service serializes as strings.
export type CostGranularity = "day" | "week" | "month";

// `source` is the cost pipeline's data source, e.g. "Bedrock LLM Events".
export interface CostSource {
  source: string;
  cost: string;
}

export interface ProjectCostType {
  costType: string;
  cost: string;
  sources: CostSource[];
}

export interface CostPeriod {
  // yyyy-MM-dd: the day, the Monday of the week, or the first of the month.
  periodStart: string;
  total: string;
  costTypes: ProjectCostType[];
}

// `userId` is null for cost not attributed to a user.
export interface UserCost {
  userId: string | null;
  userName: string | null;
  cost: string;
}

export interface ProjectCostBreakdownResponse {
  resourceType: string;
  resourceId: string;
  currency: string;
  total: string;
  granularity: CostGranularity;
  // Every period in the range, oldest first, including ones with no cost.
  periods: CostPeriod[];
  // Largest first.
  users: UserCost[];
  earliestCostDate: string;
  latestCostDate: string;
}

export interface DateRange {
  startDate: string;
  // Exclusive, matching the cost service's `end_date`.
  endDate: string;
}

const isoDate = (date: Date) => format(date, "yyyy-MM-dd");

// The `months` calendar months ending with the one containing `today`, up to
// and including `today`.
export const monthsToDate = (months: number, today: Date): DateRange => ({
  startDate: isoDate(subMonths(startOfMonth(today), months - 1)),
  endDate: isoDate(addDays(today, 1)),
});

const AI_AGENT = "ai-agent";
// Split AI agent cost is keyed `ai-agent:<source>`.
const AI_AGENT_SOURCE_PREFIX = `${AI_AGENT}:`;

const AI_AGENT_SOURCE_LABELS: Record<string, string> = {
  "Bedrock LLM Events": "LLM calls",
  "AI Lambda Events": "Code sandbox",
  // The agent's data-gateway queries.
  Snowflake: "Data queries",
};

// The cost types the cost pipeline emits, in their fixed color-slot order, so
// a cost type keeps its color whichever period is selected.
const KNOWN_COST_TYPES = [
  "compute-env",
  AI_AGENT,
  ...Object.keys(AI_AGENT_SOURCE_LABELS).map((source) => AI_AGENT_SOURCE_PREFIX + source),
  "storage",
  "workflow",
];

const COST_TYPE_LABELS: Record<string, string> = {
  "compute-env": "Compute environments",
  [AI_AGENT]: "AI agents",
  storage: "Storage",
  workflow: "Workflows",
};

export const costTypeLabel = (costType: string) => {
  if (costType.startsWith(AI_AGENT_SOURCE_PREFIX)) {
    const source = costType.slice(AI_AGENT_SOURCE_PREFIX.length);
    return `${COST_TYPE_LABELS[AI_AGENT]} · ${AI_AGENT_SOURCE_LABELS[source] ?? source}`;
  }
  return COST_TYPE_LABELS[costType] ?? costType;
};

export const orderCostTypes = (costTypes: string[]): string[] => {
  const unique = [...new Set(costTypes)];
  const known = KNOWN_COST_TYPES.filter((costType) => unique.includes(costType));
  const unknown = unique.filter((costType) => !KNOWN_COST_TYPES.includes(costType)).sort();
  return [...known, ...unknown];
};

export interface PeriodCostSummary {
  periodStart: string;
  total: number;
  byType: Record<string, number>;
}

export interface UserCostSummary {
  userId: string | null;
  userName: string | null;
  cost: number;
}

export interface ProjectCostSummary {
  total: number;
  currency: string;
  latestCostDate: string;
  // Cost types present in the range, in color-slot order.
  costTypes: string[];
  // Range total per cost type, largest first.
  byType: { costType: string; cost: number }[];
  periods: PeriodCostSummary[];
  // Largest first.
  users: UserCostSummary[];
}

// With `splitAiAgents`, AI agent cost is broken out per source instead of
// shown as one cost type.
export const summarizeCostBreakdown = (
  breakdown: ProjectCostBreakdownResponse,
  splitAiAgents: boolean
): ProjectCostSummary => {
  const rangeByType: Record<string, number> = {};
  const periods = breakdown.periods.map(({ periodStart, total, costTypes }) => {
    const byType: Record<string, number> = {};
    const add = (costType: string, cost: string) => {
      byType[costType] = Number(cost);
      rangeByType[costType] = (rangeByType[costType] ?? 0) + Number(cost);
    };
    for (const { costType, cost, sources } of costTypes) {
      if (splitAiAgents && costType === AI_AGENT) {
        for (const { source, cost: sourceCost } of sources) {
          add(AI_AGENT_SOURCE_PREFIX + source, sourceCost);
        }
      } else {
        add(costType, cost);
      }
    }
    return { periodStart, total: Number(total), byType };
  });

  return {
    total: Number(breakdown.total),
    currency: breakdown.currency,
    latestCostDate: breakdown.latestCostDate,
    costTypes: orderCostTypes(Object.keys(rangeByType)),
    byType: Object.entries(rangeByType)
      .map(([costType, cost]) => ({ costType, cost }))
      .sort((a, b) => b.cost - a.cost),
    periods,
    users: breakdown.users.map(({ userId, userName, cost }) => ({
      userId,
      userName,
      cost: Number(cost),
    })),
  };
};
